// Payroll Approval → Upload Bank Statement.
//
// Two calls to /salary/payments/statement: `preview` reads the statement and
// proposes a debit for every approved salary of the month (nothing is written);
// `apply` sends the same file with the rows the user kept, and the server re-runs
// the match before recording anything. Only confident matches (and bounced
// transfers) start ticked — a row that needs review waits for a person to pick.
//
// Both calls say which bank the statement is from: each bank writes transfers
// into its narrations its own way (hrms utils/statement_banks.py).
import { useEffect, useMemo, useState } from "react";
import { AlertCircle, CheckCircle, FileText, Loader2, Upload } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { InlineSelect } from "./BankExportDialog";
import PersonAvatar from "./people/PersonAvatar";
import {
  salaryApi,
  type StatementApplyResult,
  type StatementLineInfo,
  type StatementMatchResult,
  type StatementMatchRow,
  type StatementPreview,
} from "../api/salary";
import {
  formatIsoDate,
  initialStatementPicks,
  statementSelections,
} from "../utils/salaryPayment";

// Banks whose statements the server reads — the ids of STATEMENT_BANKS in hrms
// utils/statement_banks.py. The first is picked by default; adding a bank there
// means adding it here too.
export const STATEMENT_BANKS = [
  { id: "au", label: "AU Small Finance Bank" },
  { id: "kotak", label: "Kotak Mahindra Bank" },
];

const STATEMENT_FILE = /\.(pdf|xlsx|csv)$/i;

const RESULT_ORDER: StatementMatchResult[] = ["review", "matched", "returned", "not_found", "already_paid"];

const RESULT_LABEL: Record<StatementMatchResult, string> = {
  matched: "Matched",
  review: "Needs review",
  returned: "Returned",
  not_found: "Not in statement",
  already_paid: "Already paid",
};

const RESULT_TONE: Record<StatementMatchResult, string> = {
  matched: "bg-emerald-50 text-emerald-700 border-emerald-200",
  review: "bg-amber-50 text-amber-800 border-amber-200",
  returned: "bg-red-50 text-red-700 border-red-200",
  not_found: "bg-gray-100 text-gray-600 border-gray-200",
  already_paid: "bg-sky-50 text-sky-700 border-sky-200",
};

const REASON_LABEL: Record<string, string> = {
  amount: "amount",
  ifsc: "IFSC",
  account: "account no.",
  name: "name",
  month: "month in remark",
};

// Paise print in full (₹20,417.40) so the gap to the whole-rupee transfer is
// visible; whole amounts stay ₹20,417.
const money = (amount?: number | null) => {
  const value = Number(amount) || 0;
  const digits = Number.isInteger(value) ? 0 : 2;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
};

const LineSummary = ({ line }: { line: StatementLineInfo }) => (
  <div className="min-w-0">
    <div className="font-medium text-gray-900 whitespace-nowrap">
      {money(line.amount)} · {formatIsoDate(line.txn_date)}
    </div>
    <div className="text-xs text-gray-500 break-all" title={line.narration}>
      {[line.kind !== "OTHER" ? line.kind : null, line.beneficiary, line.utr].filter(Boolean).join(" · ") ||
        line.narration}
    </div>
  </div>
);

// Three columns so the table fits the square dialog: the employee cell also
// carries the net salary and the result.
function MatchRow({
  row,
  pick,
  onPick,
}: {
  row: StatementMatchRow;
  pick: string;
  onPick: (key: string) => void;
}) {
  const tickable = row.result === "matched" || row.result === "returned";
  const tickKey = row.result === "returned" ? row.returned?.key ?? "" : row.candidates[0]?.key ?? "";

  return (
    <tr className={pick ? "bg-emerald-50/40" : ""}>
      <td className="px-3 py-2.5 align-top">
        {tickable && (
          <input
            type="checkbox"
            aria-label={`Record ${row.name}`}
            checked={Boolean(pick)}
            onChange={(e) => onPick(e.target.checked ? tickKey : "")}
            className="h-4 w-4 cursor-pointer accent-emerald-600"
          />
        )}
      </td>
      <td className="px-3 py-2.5 align-top">
        <div className="flex items-start gap-2.5">
          <PersonAvatar employeeId={row.emptID} employeeCode={row.employeeID} name={row.name} size={24} />
          <div className="min-w-0">
            <div className="font-semibold text-gray-900">{row.name}</div>
            <div className="text-xs text-gray-500">ID: {row.employeeID}</div>
            <div className="text-xs text-gray-500 whitespace-nowrap">Net {money(row.net_salary)}</div>
            <span
              className={`mt-1.5 inline-flex whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-semibold ${RESULT_TONE[row.result]}`}
            >
              {RESULT_LABEL[row.result]}
            </span>
          </div>
        </div>
      </td>
      <td className="px-3 py-2.5 align-top">
        {row.result === "matched" && row.candidates[0] && <LineSummary line={row.candidates[0]} />}

        {row.result === "review" && (
          <div className="space-y-2">
            {row.candidates.map((candidate) => (
              <label key={candidate.key} className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`statement-pick-${row.salary_id}`}
                  checked={pick === candidate.key}
                  onChange={() => onPick(candidate.key)}
                  className="mt-1 accent-emerald-600"
                />
                <div className="min-w-0">
                  <LineSummary line={candidate} />
                  <div className="text-[11px] text-gray-400">
                    Matches on {candidate.reasons.map((r) => REASON_LABEL[r] || r).join(", ")}
                  </div>
                </div>
              </label>
            ))}
            <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-500">
              <input
                type="radio"
                name={`statement-pick-${row.salary_id}`}
                checked={!pick}
                onChange={() => onPick("")}
                className="accent-emerald-600"
              />
              Don't record
            </label>
          </div>
        )}

        {row.result === "returned" && row.returned && (
          <div>
            <LineSummary line={row.returned} />
            <div className="mt-0.5 text-xs text-red-700">
              Came back{row.returned.returned_on ? ` on ${formatIsoDate(row.returned.returned_on)}` : ""}:{" "}
              {row.returned.reason || "no reason given"}
            </div>
          </div>
        )}

        {row.result === "matched" && row.returned && (
          <div className="mt-0.5 text-xs text-amber-700">
            An earlier transfer came back ({row.returned.reason || "returned"}); this one replaced it.
          </div>
        )}

        {row.result === "not_found" && (
          <span className="text-xs text-gray-500">No debit of this amount for this employee</span>
        )}

        {row.result === "already_paid" && row.payment && (
          <span className="text-xs text-gray-600">
            {[money(row.payment.amount), formatIsoDate(row.payment.txn_date), row.payment.utr]
              .filter(Boolean)
              .join(" · ")}
          </span>
        )}

        {row.possible_return && (
          <div className="mt-1 text-xs text-amber-700">
            {money(row.possible_return.amount)} came back on {formatIsoDate(row.possible_return.txn_date)} without
            saying which transfer it was — check it wasn't this salary before recording it.
          </div>
        )}
      </td>
    </tr>
  );
}

export function BankStatementUploadDialog({
  open,
  onOpenChange,
  year,
  month,
  periodLabel,
  onApplied,
  confirmClassName = "",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  year: string;
  month: string;
  periodLabel: string;
  onApplied: (result: StatementApplyResult) => void;
  confirmClassName?: string;
}) {
  const [bank, setBank] = useState(STATEMENT_BANKS[0].id);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<StatementPreview | null>(null);
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<"" | "reading" | "saving">("");
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);

  // Every opening starts clean — a statement checked for another month must
  // never be applied to this one.
  useEffect(() => {
    if (!open) return;
    setBank(STATEMENT_BANKS[0].id);
    setFile(null);
    setPreview(null);
    setPicks({});
    setBusy("");
    setError("");
    setDragging(false);
  }, [open, year, month]);

  const rows = useMemo(
    () =>
      [...(preview?.rows ?? [])].sort(
        (a, b) =>
          RESULT_ORDER.indexOf(a.result) - RESULT_ORDER.indexOf(b.result) ||
          (a.name || "").localeCompare(b.name || "")
      ),
    [preview]
  );

  const bankLabel = STATEMENT_BANKS.find((b) => b.id === bank)?.label ?? bank;
  const selections = statementSelections(picks);
  const paidCount = rows.filter((r) => r.result !== "returned" && picks[r.salary_id]).length;
  const returnedCount = rows.filter((r) => r.result === "returned" && picks[r.salary_id]).length;

  // A preview read as another bank must never be applied, so it goes too.
  const chooseBank = (next: string) => {
    if (busy !== "") return;
    setBank(next);
    setPreview(null);
    setPicks({});
    setError("");
  };

  const chooseFile = (next: File | null) => {
    if (next && !STATEMENT_FILE.test(next.name)) {
      setError("That file isn't a PDF, XLSX or CSV statement.");
      return;
    }
    setFile(next);
    setPreview(null);
    setPicks({});
    setError("");
  };

  const checkStatement = async () => {
    if (!file) return;
    setBusy("reading");
    setError("");
    try {
      const data = await salaryApi.previewBankStatement(year, month, bank, file);
      setPreview(data);
      setPicks(initialStatementPicks(data.rows));
    } catch (err: any) {
      setPreview(null);
      setError(err?.response?.data?.detail || err?.message || "Couldn't read the statement");
    } finally {
      setBusy("");
    }
  };

  const record = async () => {
    if (!file || selections.length === 0) return;
    setBusy("saving");
    setError("");
    try {
      const result = await salaryApi.applyBankStatement(year, month, bank, file, selections);
      onApplied(result);
      onOpenChange(false);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || "Couldn't record the payments");
    } finally {
      setBusy("");
    }
  };

  const setPick = (salaryId: string, key: string) =>
    setPicks((prev) => {
      const next = { ...prev };
      if (key) next[salaryId] = key;
      else delete next[salaryId];
      return next;
    });

  const confirmLabel =
    paidCount && returnedCount
      ? `Mark ${paidCount} paid · ${returnedCount} returned`
      : returnedCount
      ? `Record ${returnedCount} returned`
      : paidCount
      ? `Mark ${paidCount} paid`
      : "Mark paid";

  return (
    <Dialog open={open} onOpenChange={(next) => busy === "" && onOpenChange(next)}>
      {/* Square from sm up: 32rem each way, but never taller than the window
          (a min-height above max-h-[90vh] would win and push the dialog off the
          top and bottom of a short screen); results scroll inside it.
          A file dropped anywhere on it is taken as the statement; without these
          handlers the browser would open the dropped PDF in place of the app. */}
      <DialogContent
        className="max-w-[95%] sm:max-w-lg sm:min-h-[min(32rem,90vh)] max-h-[90vh] grid-rows-[auto_1fr] rounded-lg overflow-y-auto"
        onDragOver={(e) => {
          e.preventDefault();
          if (busy === "") setDragging(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const dropped = e.dataTransfer?.files?.[0];
          if (dropped && busy === "") chooseFile(dropped);
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg font-semibold flex items-center gap-2">
            <Upload className="w-5 h-5 text-emerald-700" />
            Upload Bank Statement
          </DialogTitle>
          <DialogDescription>
            Approved salaries for {periodLabel} are matched to the debits on your bank statement. Nothing is marked
            paid until you confirm.
          </DialogDescription>
        </DialogHeader>

        {/* min-w-0: DialogContent is a grid, and the one-line file name would
            otherwise stretch its column past the dialog on a phone. */}
        <div className="mt-2 min-w-0 flex flex-col gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Bank</label>
            <InlineSelect
              value={bank}
              onChange={chooseBank}
              options={STATEMENT_BANKS.map((b) => ({ value: b.id, label: b.label }))}
              ariaLabel="Bank"
              placeholder="Select a bank…"
            />
          </div>

          {/* Fills the square's spare height until a statement has been checked. */}
          <label
            className={`${preview ? "" : "flex-1"} min-h-[7rem] min-w-0 flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed px-4 py-4 text-center cursor-pointer transition-colors ${
              dragging ? "border-emerald-500 bg-emerald-50" : "border-gray-300 hover:bg-gray-50"
            }`}
          >
            <FileText className={`w-8 h-8 mb-1 ${file ? "text-emerald-700" : "text-gray-400"}`} />
            {file ? (
              <>
                <span className="max-w-full truncate text-sm font-semibold text-gray-900">{file.name}</span>
                <span className="text-xs text-gray-500">Click or drop another file to replace it</span>
              </>
            ) : (
              <>
                <span className="text-sm font-medium text-gray-700">Choose the {bankLabel} statement</span>
                <span className="text-xs text-gray-500">
                  PDF, XLSX or CSV from net banking · click to browse or drop it here
                </span>
              </>
            )}
            <input
              type="file"
              accept=".pdf,.xlsx,.csv"
              className="sr-only"
              aria-label="Bank statement file"
              onChange={(e) => {
                chooseFile(e.target.files?.[0] ?? null);
                e.target.value = "";
              }}
            />
          </label>

          <Button
            onClick={checkStatement}
            disabled={!file || busy !== ""}
            className={`w-full text-sm ${confirmClassName}`}
          >
            {busy === "reading" ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            {preview ? "Check again" : "Check statement"}
          </Button>

          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {preview && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500 mr-1">
                  {bankLabel} · {preview.statement.transactions} transactions ·{" "}
                  {formatIsoDate(preview.statement.from)} – {formatIsoDate(preview.statement.to)}
                </span>
                {RESULT_ORDER.filter((key) => preview.summary[key] > 0).map((key) => (
                  <span
                    key={key}
                    className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${RESULT_TONE[key]}`}
                  >
                    {RESULT_LABEL[key]}: {preview.summary[key]}
                  </span>
                ))}
              </div>

              {rows.length === 0 ? (
                <p className="text-sm text-gray-500">There are no approved salaries for {periodLabel}.</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left">
                      <tr className="text-xs font-bold uppercase tracking-wide text-gray-600">
                        <th className="px-3 py-2 w-10" />
                        <th className="px-3 py-2">Employee</th>
                        <th className="px-3 py-2">Bank Transaction</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {rows.map((row) => (
                        <MatchRow
                          key={row.salary_id}
                          row={row}
                          pick={picks[row.salary_id] || ""}
                          onPick={(key) => setPick(row.salary_id, key)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {preview.unmatched_salary_debits.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <div className="font-semibold mb-1">
                    Salary debits on the statement that fit no approved salary
                  </div>
                  <ul className="space-y-0.5">
                    {preview.unmatched_salary_debits.map((line) => (
                      <li key={line.key}>
                        {formatIsoDate(line.txn_date)} · {money(line.amount)} ·{" "}
                        {line.beneficiary || line.narration}
                        {line.utr ? ` · ${line.utr}` : ""}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-1 text-amber-800">
                    Usually the amount changed after the sheet was exported, or bank details are missing. Use
                    Mark paid on that employee's row instead.
                  </div>
                </div>
              )}
            </>
          )}

          <div className="mt-auto flex gap-2 sm:gap-3 pt-1">
            <Button
              onClick={() => onOpenChange(false)}
              disabled={busy !== ""}
              variant="outline"
              className="flex-1 text-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={record}
              disabled={!preview || selections.length === 0 || busy !== ""}
              className={`flex-1 text-sm ${confirmClassName}`}
            >
              {busy === "saving" ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              {confirmLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
